import {
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { colors } from "../../theme/theme";
import {
  Password,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";

export const CustomInput = ({
  label,
  placeholder,
  value,
  handleChange,
  type,
  Icon,
  onClick,
  noIcon,
  rightIcon,
  mT,
  mB,
  bR,
  width,
  onKeyDown = null,
  color,
  borderColor,
  height,
  textArea = false,
  SecondaryIcon = null,
  secondaryIconOnClick = null,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <FormControl fullWidth sx={{ mt: mT || 0, mb: mB || 0 }}>
      {label && (
        <Typography
          sx={{
            fontFamily: "Mulish-Regular",
            fontSize: "12px",
            color: color || colors.textColor,
            fontWeight: "700",
            mb: 0.5,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
      )}

      <TextField
        multiline={textArea}
        rows={textArea ? 4 : 1}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "transparent",
            fontFamily: "Mulish-Regular",
            fontSize: "14px",
            fontWeight: "400",
            borderRadius: bR || "9px",
            width: width || "75%",
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: bR || "9px",
              border: `1px solid  ${borderColor || "rgba(25,32,49, 0.14)"}`,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor || colors.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.secondary,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-input": {
              color: color || colors.primary,
            },
          },
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (onClick) {
              onClick();
            }
          }
        }}
        type={type || "text"}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        InputProps={{
          sx: {
            height: height || textArea ? "auto" : "50px",
            "& .MuiOutlinedInput-root": {
              borderRadius: bR || "500px",
            },
          },
          startAdornment: (
            <>
              {!noIcon && !rightIcon && (
                <InputAdornment
                  position="start"
                  sx={{
                    paddingRight: "5px",
                  }}
                >
                  <Icon
                    sx={{
                      color: isFocused
                        ? colors.secondary
                        : color || colors.textColor,
                      fontSize: "20px",
                    }}
                  />
                </InputAdornment>
              )}
            </>
          ),
          endAdornment: (
            <>
              {!noIcon && rightIcon && (
                <InputAdornment position="start" sx={{ marginRight: "-5px" }}>
                  {SecondaryIcon && (
                    <IconButton onClick={secondaryIconOnClick || null}>
                      <SecondaryIcon
                        sx={{
                          color: color || colors.primary,
                          fontSize: "20px",
                        }}
                      />
                    </IconButton>
                  )}
                  <IconButton onClick={onClick || null}>
                    <Icon
                      sx={{
                        color: color || colors.primary,
                        fontSize: "20px",
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              )}
            </>
          ),
        }}
        InputLabelProps={{
          shrink: false,
        }}
      />
    </FormControl>
  );
};

export const CustomPasswordInput = ({
  label,
  placeholder,
  value,
  handleChange,
  type,
  mT,
  mB,
  bR,
  width,
  onKeyDown = null,
  color,
  borderColor,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  return (
    <FormControl fullWidth sx={{ mt: mT || 0, mb: mB || 0 }}>
      {label && (
        <Typography
          sx={{
            fontFamily: "Mulish-Regular",
            fontSize: "12px",
            color: color || colors.textColor,
            fontWeight: "700",
            mb: 0.5,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
      )}

      <TextField
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "transparent",
            fontFamily: "Mulish-Regular",
            fontSize: "14px",
            fontWeight: "400",
            borderRadius: bR || "9px",
            width: width || "75%",
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: bR || "9px",
              border: `1px solid  ${borderColor || "rgba(25,32,49, 0.14)"}`,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor || colors.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.secondary,
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-input": {
              color: color || colors.primary,
            },
          },
        }}
        onKeyDown={onKeyDown || null}
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        InputProps={{
          sx: {
            height: "50px",
            "& .MuiOutlinedInput-root": {
              borderRadius: bR || "500px",
            },
          },
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                paddingRight: "5px",
              }}
            >
              <Password
                sx={{
                  color: isFocused
                    ? colors.secondary
                    : color || colors.textColor,
                  fontSize: "20px",
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {!showPassword ? (
                  <VisibilityOffOutlined
                    sx={{ color: color || "rgba(25,32,49,0.5)" }}
                  />
                ) : (
                  <VisibilityOutlined
                    sx={{ color: color || "rgba(25,32,49,0.5)" }}
                  />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: false,
        }}
      />
    </FormControl>
  );
};
